/**
 * The figures in play (spec 05 §6): a puppet for each player, monster and Baron the simulation
 * has, made when it first appears and dropped when it goes, placed between the last two sim
 * frames. A monster has two puppets, calm and angry, and swaps material when it is trapped so it
 * sits inside its bubble (glassBubble.ts explains the depth bias).
 */

import { type Material, Mesh, type Object3D, type Scene, Vector3 } from 'three';
import { FRAMES_PER_PASS } from '../../sim/tuning';
import { playerPose } from '../../sim/players';
import { monsterPose } from '../../sim/monsters';
import { deathFlight } from '../../sim/bubbles/items';
import { bubbleLook } from '../../sim/bubbles/view';
import type { Bubble, Item, Monster, Player, World } from '../../sim/types';
import { buildDragon } from '../actors/dragon';
import { buildMonster, buildSkelMonsta } from '../actors/monsters';
import type { Puppet } from '../actors/puppet';
import type { Palette } from '../palette';
import { blinkShown } from './flash';
import { poseDragonFor, poseMonster } from './poses';
import { centreToWorld, feetToWorld, lerpCoord } from './simToWorld';

export interface FigureMaterials {
  player: Material;
  /** A player under the power heart flashes between this and its own colours. */
  heart: Material;
  monster: Material;
  trapped: Material;
  /** All light blue: a dead monster flying off to become its fruit. */
  dying: Material;
  /** The colour aid's angry monster: its own colours, banded (materials/aid.ts). */
  angry: Material;
  /** Reduce flashing's power heart: the player's own colours under a gentle pink glow (flash.ts). */
  heartSoft: Material;
  /** Reduce flashing's respawn: the player thinned by a dither, steady, instead of blinking 25 times a second. */
  ghost: Material;
}

/** The display options the figures follow (08 §4). */
export interface FigureOptions {
  colourAid: boolean;
  reduceFlashing: boolean;
}

/** Positions at the previous sim frame, by entity key, for interpolation. */
export type Previous = Map<string, { x: number; y: number }>;

interface MonsterView {
  calm: Puppet;
  angry: Puppet;
}

function setMaterial(root: Object3D, m: Material): void {
  root.traverse((o) => {
    if (o instanceof Mesh) o.material = m;
  });
}

export class EntityViews {
  private readonly players: [Puppet, Puppet];
  private readonly monsters = new Map<number, MonsterView>();
  private readonly barons = new Map<number, Puppet>();
  private readonly tmp = new Vector3();
  options: FigureOptions = { colourAid: false, reduceFlashing: false };

  constructor(private readonly scene: Scene, private readonly p: Palette, private readonly mats: FigureMaterials) {
    this.players = [buildDragon(p, 'bub', mats.player), buildDragon(p, 'bob', mats.player)];
    for (const pup of this.players) scene.add(pup.root);
  }

  private place(pup: Puppet, key: string, x: number, y: number, prev: Previous, alpha: number): void {
    const before = prev.get(key) ?? { x, y };
    feetToWorld(lerpCoord(before.x, x, alpha), lerpCoord(before.y, y, alpha), this.tmp);
    pup.root.position.copy(this.tmp);
  }

  private syncPlayer(pl: Player, prev: Previous, alpha: number, t: number, heart: boolean): void {
    const pup = this.players[pl.id];
    const pose = playerPose(pl);
    // Respawn invulnerability: the C64 flashes the sprite every I-tick (P1 blinks out); ours
    // blinks both, as the arcade does. Under the power heart it flashes pink instead (heartFlash).
    // With reduce flashing it does not blink at all: it is drawn thinned (dressPlayer).
    pup.root.visible = pose.pose !== 'hidden' && (heart || this.options.reduceFlashing || !pose.flash);
    if (pose.pose === 'hidden') return;
    this.place(pup, `p${pl.id}`, pl.x, pl.y, prev, alpha);
    poseDragonFor(pup, pl, t);
  }

  /**
   * Under the power heart a player flashes (the C64 flips its colour each tick; a steadier 4 Hz here);
   * with reduce flashing it glows pink instead, pulsing gently (the glow is set by FigureSet), and a
   * respawning player is thinned rather than blinked.
   */
  private dressPlayer(pl: Player, heart: boolean, t: number): void {
    const reduce = this.options.reduceFlashing;
    let look: keyof FigureMaterials = 'player';
    if (heart) look = reduce ? 'heartSoft' : blinkShown(t, 4, false) ? 'heart' : 'player';
    else if (reduce && pl.invulnerable > 0 && pl.state === 'play') look = 'ghost';
    this.dressAs(this.players[pl.id], look);
  }

  private monsterView(m: Monster): MonsterView {
    let v = this.monsters.get(m.id);
    if (!v) {
      v = { calm: buildMonster(m.kind, false, this.p, this.mats.monster), angry: buildMonster(m.kind, true, this.p, this.mats.monster) };
      this.scene.add(v.calm.root, v.angry.root);
      this.monsters.set(m.id, v);
    }
    return v;
  }


  private dressAs(pup: Puppet, look: keyof FigureMaterials): void {
    if (pup.root.userData['look'] === look) return;
    setMaterial(pup.root, this.mats[look]);
    pup.root.userData['look'] = look;
  }

  /**
   * A monster: calm or angry; inside its bubble when trapped (the water carries it as itself, with
   * no bubble: `bubbleId` -1); once dead, not drawn - unless its death item is still flying, when it
   * is drawn there, spinning and light blue, as the C64 draws it.
   */
  private syncMonster(
    m: Monster, prev: Previous, alpha: number, soft: number, t: number, flying: Map<number, Item>, bubbles: Map<number, Bubble>,
    heart: boolean,
  ): void {
    const v = this.monsterView(m);
    const pup = m.angry ? v.angry : v.calm;
    const other = m.angry ? v.calm : v.angry;
    other.root.visible = false;
    const flight = m.state === 'dead' ? flying.get(m.id) : undefined;
    pup.root.visible = m.state !== 'dead' || flight !== undefined;
    if (!pup.root.visible) return;
    if (flight) {
      this.dressAs(pup, 'dying');
      this.place(pup, `i${flight.id}`, flight.x, flight.y, prev, alpha);
      pup.root.scale.setScalar(1);
      pup.facing.rotation.y = t * 14;
      return;
    }
    const bubble = m.state === 'trapped' ? bubbles.get(m.bubbleId) : undefined;
    // Under the power heart every monster out of a bubble is frozen light blue (02 §7.5). The colour
    // aid bands an angry one (it has a puppet of its own, so only it changes material).
    const free = m.angry && this.options.colourAid ? 'angry' : 'monster';
    this.dressAs(pup, bubble ? 'trapped' : heart && monsterPose(m) === 'frozen' ? 'dying' : free);
    if (bubble) this.inBubble(pup, bubble, prev, soft);
    else this.place(pup, `m${m.id}`, m.x, m.y, prev, alpha);
    pup.root.scale.setScalar(bubble ? 0.8 : 1);
    poseMonster(pup, m, t);
  }

  /**
   * A trapped monster rides its bubble: placed from the bubble's own glide (the M-tick clock), so the
   * two move as one - the sim puts the monster on its bubble a pass late (D_3CB2), and on the sprite
   * clock it would slip about inside the glass. It sits in the middle of the bubble, not on its feet.
   */
  private inBubble(pup: Puppet, b: Bubble, prev: Previous, soft: number): void {
    const look = bubbleLook(b);
    const before = prev.get(`u${b.id}`) ?? look;
    centreToWorld(lerpCoord(before.x, look.x, soft), lerpCoord(before.y, look.y, soft), this.tmp);
    pup.root.position.set(this.tmp.x, this.tmp.y - 0.55, this.tmp.z);
  }

  sync(world: World, prev: Previous, alpha: number, softAlpha: number, t: number): void {
    for (const pl of world.players) {
      const heart = world.itemSystem?.heartOn[pl.id] === true && pl.state === 'play';
      this.syncPlayer(pl, prev, alpha, t, heart);
      this.dressPlayer(pl, heart, t);
    }
    const bubbles = new Map<number, Bubble>();
    for (const b of world.bubbles) bubbles.set(b.id, b);
    const flying = new Map<number, Item>();
    for (const i of world.items) {
      const f = deathFlight(i);
      if (f && f.stage !== 'item') flying.set(f.monsterId, i);
    }
    const seen = new Set<number>();
    // No monster shows until the fly-in is over and the drop-in begins (02 §9.1).
    const hidden = world.flow.phase === 'flyIn';
    for (const m of world.monsters) {
      seen.add(m.id);
      const v = hidden ? this.monsters.get(m.id) : undefined;
      if (v) v.calm.root.visible = v.angry.root.visible = false;
      if (hidden) continue;
      this.syncMonster(m, prev, alpha, softAlpha, t, flying, bubbles, world.power.heart >= 0);
    }
    for (const [id, v] of this.monsters) {
      if (seen.has(id)) continue;
      this.scene.remove(v.calm.root, v.angry.root);
      v.calm.dispose();
      v.angry.dispose();
      this.monsters.delete(id);
    }
    this.syncBarons(world, prev, softAlpha, t);
  }

  private syncBarons(world: World, prev: Previous, alpha: number, t: number): void {
    const seen = new Set<number>();
    for (const b of world.barons) {
      seen.add(b.id);
      let pup = this.barons.get(b.id);
      if (!pup) {
        pup = buildSkelMonsta(this.p, this.mats.monster);
        this.scene.add(pup.root);
        this.barons.set(b.id, pup);
      }
      pup.root.visible = true;
      this.place(pup, `b${b.id}`, b.x, b.y, prev, alpha);
      pup.turnTo(this.players[b.target].root.position.x < pup.root.position.x ? -1 : 1, t);
    }
    for (const [id, pup] of this.barons) {
      if (seen.has(id)) continue;
      this.scene.remove(pup.root);
      pup.dispose();
      this.barons.delete(id);
    }
  }

  /** Take everything out of the scene and free its geometry (the materials are the caller's). */
  dispose(): void {
    this.clear();
    for (const pup of this.players) {
      this.scene.remove(pup.root);
      pup.dispose();
    }
  }

  /** Drop every monster and Baron; the dragons stay. */
  clear(): void {
    for (const v of this.monsters.values()) {
      this.scene.remove(v.calm.root, v.angry.root);
      v.calm.dispose();
      v.angry.dispose();
    }
    this.monsters.clear();
    for (const pup of this.barons.values()) {
      this.scene.remove(pup.root);
      pup.dispose();
    }
    this.barons.clear();
  }
}

/**
 * Before a sim frame: record where things are if this frame is about to move them, so the view
 * can glide from there. Sprites (players, monsters, items) move on I-ticks, every 2nd frame; the
 * bubbles and the Baron on M-ticks, every 3rd (spec 02 §1.1) - each is interpolated across its
 * own clock's interval, or it would judder.
 */
export function snapshot(world: World, into: Previous): void {
  const c = world.clock;
  const next = c.frame + 1;
  if ((next & 1) === 1) {
    for (const p of world.players) into.set(`p${p.id}`, { x: p.x, y: p.y });
    for (const m of world.monsters) into.set(`m${m.id}`, { x: m.x, y: m.y });
    for (const i of world.items) into.set(`i${i.id}`, { x: i.x, y: i.y });
    if (world.boss) into.set('boss', { x: world.boss.x, y: world.boss.y });
  }
  if (next >= c.nextPass) {
    for (const b of world.barons) into.set(`b${b.id}`, { x: b.x, y: b.y });
    for (const s of world.projectiles) into.set(`j${s.id}`, { x: s.x, y: s.y });
    // Where each bubble is drawn (`bubbleLook`), which is not always its x, y (sim/bubbles/README.md).
    for (const b of world.bubbles) {
      const look = bubbleLook(b);
      into.set(`u${b.id}`, { x: look.x, y: look.y });
    }
  }
}

/** How far through their clocks' intervals sprites and bubbles are, given the frame fraction `acc`. */
export function tickAlphas(world: World, acc: number): { sprite: number; soft: number } {
  const c = world.clock;
  const sprite = (((c.frame & 1) === 1 ? 0 : 1) + acc) / 2;
  // A pass is FRAMES_PER_PASS frames (steady; with the C64 slowdown option it can be longer,
  // and the bubbles then glide a little early - harmless).
  const since = Math.max(0, c.frame - (c.nextPass - FRAMES_PER_PASS));
  return { sprite: Math.min(1, sprite), soft: Math.min(1, (since + acc) / FRAMES_PER_PASS) };
}
